import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ArrowLeft, LogOut, ShieldCheck } from "lucide-react";
import { CompaniesPanel } from "@/components/owner/CompaniesPanel";
import { AccountsPanel } from "@/components/owner/AccountsPanel";
import { LoginAttemptsPanel } from "@/components/owner/LoginAttemptsPanel";
import { ActivityPanel } from "@/components/owner/ActivityPanel";
import { RecoveryEmailPanel } from "@/components/owner/RecoveryEmailPanel";

const TABS = [
  ["companies", "Companies"],
  ["accounts", "Accounts"],
  ["signins", "Failed sign-ins"],
  ["activity", "Activity log"],
  ["recovery", "Recovery & protection"],
];

export default function OwnerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("companies");
  const [filter, setFilter] = useState(null);

  if (!user?.is_owner) return <Navigate to="/employer" replace />;

  const openCompany = (c) => { setFilter({ id: c.id || "none", name: c.name }); setTab("accounts"); };

  return (
    <div className="min-h-screen bg-slate-50 font-body" data-testid="owner-dashboard">
      <header className="bg-indigo-950 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-8 pb-14">
          <div className="flex items-center justify-between gap-4 mb-10">
            <Button variant="ghost" size="sm" onClick={() => navigate("/employer")} className="text-indigo-200 hover:text-white hover:bg-white/10 -ml-3" data-testid="owner-back-to-dispatch-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Dispatch
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} className="text-indigo-200 hover:text-white hover:bg-white/10" data-testid="owner-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
          <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-lime-400 mb-3 owner-rise">
            <ShieldCheck className="w-4 h-4" /> Owner · protected account
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight owner-rise" style={{ animationDelay: "60ms" }}>Platform overview</h1>
          <p className="text-indigo-200 text-base md:text-lg mt-3 owner-rise" style={{ animationDelay: "120ms" }} data-testid="owner-signed-in-as">
            Signed in as {user.name} · {user.email}
          </p>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-8 -mt-6 pb-16">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-auto flex flex-wrap justify-start gap-1 bg-white border border-slate-200 shadow-sm rounded-2xl p-1.5 mb-8">
            {TABS.map(([value, label]) => (
              <TabsTrigger key={value} value={value} data-testid={`owner-tab-${value}`}
                className="rounded-xl px-4 py-2 text-sm font-semibold data-[state=active]:bg-indigo-950 data-[state=active]:text-white transition-colors">
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="companies"><CompaniesPanel onOpen={openCompany} /></TabsContent>
          <TabsContent value="accounts"><AccountsPanel filter={filter} onClearFilter={() => setFilter(null)} /></TabsContent>
          <TabsContent value="signins"><LoginAttemptsPanel /></TabsContent>
          <TabsContent value="activity"><ActivityPanel /></TabsContent>
          <TabsContent value="recovery"><RecoveryEmailPanel /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
